import { DataLogStatus } from 'store/app/enums';
import { Provider } from 'store/api/enums';
import { MappedReturn } from '../../returns/models/returnData';
import { Entity, Mapping, Organisation } from '../models';
import { calculateNextPeriod } from 'utils/calculate-next-period';

export function addEntitiesMapping(
  organisations?: Organisation[],
  mappings?: Mapping[]
): Entity[] {
  const entities: Entity[] = [];
  organisations?.forEach((c) => {
    const receiptScanStepsCompleted = c.receiptScanStepsCompleted;
    const providerName = c.providerName;
    const organisationId = c.uuid;
    const organisation = c.friendlyName;
    const vatNumber = c.taxNumber;
    const country = c.country;
    const currency = c.currency;
    const groupedEntityIds =
      c.providerName === Provider.GROUP
        ? c.groupedConnections
            ?.map((groupedConnection) => groupedConnection.uuid)
            .filter(Boolean)
        : undefined;
    const connectionId = c.connectionId;
    const shortCode = c.shortCode;
    const returnTypes = c.returnTypes;
    const returnTypesEdit = c.returnTypesEdit;
    const taxRates = c.taxRates;
    const connectedProperly = c.connectedProperly;
    const hasTaxRates = c.hasTaxRates;
    const operationRights = c.operationRights;
    const entityStatus = c.status;
    c.users.forEach((user) => {
      user.fullName = `${user.firstName} ${user.lastName}`;
    });
    const users = c.users;

    const tempMappings = mappings?.filter((m) => m.organisationId === c.uuid);
    const returns: MappedReturn[] = [];
    if (c.providerName !== Provider.CIN7) {
      tempMappings?.forEach((mappedReturn, idx) => {
        const retDueDate = mappedReturn?.returnDueDate
          ? mappedReturn?.returnDueDate
          : '';

        // For entities view, use first non-cancelled dataLog if available
        const dataLogs = mappedReturn.dataLogs || [];
        const activeDataLog = dataLogs.find(
          (dl) => DataLogStatus[dl.status] !== DataLogStatus.CANCELLED
        );

        const map: MappedReturn = {
          key: idx,
          mappingId: mappedReturn.mappingId,
          mappingName:
            c.returnTypesEdit?.find((rt) => rt.id === mappedReturn.returnTypeId)
              ?.countryName +
            ' - ' +
            mappedReturn.mappingName,
          nextDueDate: new Date(retDueDate).toLocaleDateString(),
          status: activeDataLog ? DataLogStatus[activeDataLog.status] : '',
          canDelete: mappedReturn.canDelete,
          hasMapping: true,
          dataLog: activeDataLog ? activeDataLog : undefined,
          connectionId: c.connectionId,
          sections: mappedReturn.sections,
          returnTypeId: mappedReturn.returnTypeId,
          connectedProperly,
          returnFrequency: mappedReturn.returnFrequency,
          operationRights,
          entityStatus,
        };

        returns.push(map);
      });

      entities.push({
        providerName,
        organisationId,
        organisation,
        vatNumber,
        country,
        currency,
        groupedEntityIds,
        connectionId,
        shortCode,
        returns,
        key: entities.length + 1,
        users,
        returnTypes,
        returnTypesEdit,
        taxRates,
        connectedProperly,
        operationRights,
        entityStatus,
        hasTaxRates,
        receiptScanStepsCompleted,
      });
    }
  });
  return entities;
}

export function addMapping(
  organisations?: Organisation[],
  mappings?: Mapping[]
): MappedReturn[] {
  const mappedReturns: MappedReturn[] = [];

  organisations?.map((c) => {
    const connectedProperly = c.connectedProperly;
    const hmrcConnectionUuid = c.hmrcConnectionUuid;
    const providerName = c.providerName;
    const organisationId = c.uuid;
    const organisation = c.friendlyName;
    const vatNumber = c.taxNumber;
    const country = c.country;
    const connectionId = c.connectionId;
    const shortCode = c.shortCode;
    const returnTypes = c.returnTypes;
    const returnTypesEdit = c.returnTypesEdit;
    const entityStatus = c.status;
    const taxRates = c.taxRates;
    const operationRights = c.operationRights;
    const receiptScanStepsCompleted = c.receiptScanStepsCompleted;
    const groupedEntityIds =
      c.providerName === Provider.GROUP
        ? c.groupedConnections
            ?.map((groupedConnection) => groupedConnection.uuid)
            .filter(Boolean)
        : undefined;

    const tempMappings = mappings?.filter((m) => m.organisationId === c.uuid);
    if (c.providerName !== Provider.CIN7) {
      tempMappings?.map((m) => {
        const toDisplayDueDate = (value?: string | Date | null) =>
          value ? new Date(value).toDateString() : '';
        const retDueDate = m.returnDueDate ? m.returnDueDate : '';
        const mappingId = m.mappingId;
        const mappingName =
          c.returnTypesEdit?.find((rt) => rt.id === m.returnTypeId)
            ?.countryName +
          ' - ' +
          m.mappingName;
        const returnTypeId = m.returnTypeId;
        // Default (mapping-level) due date is used for NOT_STARTED rows.
        const nextDueDate = toDisplayDueDate(retDueDate);
        const canDelete = m.canDelete;
        const sections = m.sections;
        const returnFrequency = m.returnFrequency;
        const nextPeriod = m.nextPeriod;

        // Create a separate MappedReturn for each dataLog (excluding cancelled ones)
        const dataLogs = m.dataLogs && m.dataLogs.length > 0 ? m.dataLogs : [];

        // Add active (non-cancelled, non-submitted) dataLogs
        dataLogs.forEach((dataLog) => {
          // Skip cancelled dataLogs
          if (DataLogStatus[dataLog?.status] === DataLogStatus.CANCELLED) {
            return;
          }

          const preparer = dataLog?.preparerUser
            ? dataLog?.preparerUser
            : undefined;
          const reviewer = dataLog?.reviewerUser
            ? dataLog?.reviewerUser
            : undefined;
          const client = dataLog?.clientUser ? dataLog?.clientUser : undefined;
          const status = dataLog ? DataLogStatus[dataLog?.status] : '';
          // For started/in-progress rows prefer data-log due date when present.
          // Keep mapping due date as fallback for compatibility with older payloads.
          const dataLogDueDate =
            (dataLog as any)?.dueDate ||
            (dataLog as any)?.returnDueDate ||
            (dataLog as any)?.nextDueDate;
          const rowNextDueDate = toDisplayDueDate(dataLogDueDate || retDueDate);

          const mappedRow: MappedReturn = {
            providerName,
            hmrcConnectionUuid,
            receiptScanStepsCompleted,
            groupedEntityIds,
            organisationId,
            organisation,
            vatNumber,
            country,
            mappingId,
            mappingName,
            nextDueDate: rowNextDueDate,
            nextPeriod,
            fromReportingDate: dataLog?.fromReportingDate ?? null,
            toReportingDate: dataLog?.toReportingDate ?? null,
            preparer,
            reviewer,
            client,
            status,
            canDelete,
            key: mappedReturns.length + 1,
            connectionId,
            shortCode,
            dataLog,
            returnTypeId,
            returnTypes,
            returnTypesEdit,
            sections,
            taxRates,
            connectedProperly,
            returnFrequency,
            periodFromDate: m.periodFromDate,
            operationRights,
            entityStatus,
          };
          mappedReturns.push(mappedRow);
        });

        // Check if we need to add a "Not Started" row
        const shouldAddNotStarted = () => {
          // If no dataLogs at all, add "Not Started"
          if (dataLogs.length === 0) {
            return true;
          }

          const normalizeDataLogStatus = (rawStatus: unknown) => {
            const enumValue =
              DataLogStatus[rawStatus as keyof typeof DataLogStatus];
            return enumValue || (rawStatus as string);
          };

          // If there's already an active row (e.g. Saved/Locked/etc), don't add
          // a synthetic NOT_STARTED row for the same mapping.
          const hasActiveDataLog = dataLogs.some((dl) => {
            const status = normalizeDataLogStatus((dl as any)?.status);
            return (
              status !== DataLogStatus.CANCELLED &&
              status !== DataLogStatus.SUBMITTED
            );
          });
          if (hasActiveDataLog) {
            return false;
          }

          // Find the latest dataLog by date
          const sortedDataLogs = [...dataLogs].sort((a, b) => {
            const dateA = new Date(
              a.lastUpdatedDate || a.generatedDate || a.submissionDate || ''
            ).getTime();
            const dateB = new Date(
              b.lastUpdatedDate || b.generatedDate || b.submissionDate || ''
            ).getTime();
            const safeA = Number.isNaN(dateA) ? 0 : dateA;
            const safeB = Number.isNaN(dateB) ? 0 : dateB;
            return safeB - safeA; // Latest first
          });

          const latestDataLog = sortedDataLogs[0];

          // If latest is SUBMITTED or CANCELLED, add "Not Started" for next period
          if (
            latestDataLog &&
            (normalizeDataLogStatus(latestDataLog.status) ===
              DataLogStatus.SUBMITTED ||
              normalizeDataLogStatus(latestDataLog.status) ===
                DataLogStatus.CANCELLED)
          ) {
            return true;
          }

          return false;
        };

        if (shouldAddNotStarted()) {
          const calculatedPeriod =
            m.periodFromDate && returnFrequency
              ? calculateNextPeriod(m.periodFromDate, returnFrequency)
              : [];
          let adjustedNextPeriod = nextPeriod;
          if (calculatedPeriod.length > 0) {
            const periodStr = String(calculatedPeriod[0]);
            adjustedNextPeriod = periodStr.replace(
              /(\d{2})\/(\d{2})\/(\d{4})/g,
              (_, d, mo, y) => `${d}/${mo}/${y.slice(-2)}`
            );
          }
          mappedReturns.push({
            providerName,
            hmrcConnectionUuid,
            groupedEntityIds,
            organisationId,
            organisation,
            vatNumber,
            country,
            mappingId,
            mappingName,
            nextDueDate: nextDueDate,
            nextPeriod: adjustedNextPeriod,
            fromReportingDate: null,
            toReportingDate: null,
            preparer: undefined,
            reviewer: undefined,
            client: undefined,
            status: DataLogStatus.NOT_STARTED,
            canDelete,
            key: mappedReturns.length + 1,
            connectionId,
            shortCode,
            dataLog: undefined,
            returnTypeId,
            returnTypes,
            returnTypesEdit,
            sections,
            taxRates,
            connectedProperly,
            returnFrequency,
            periodFromDate: m.periodFromDate,
            operationRights,
            entityStatus,
          });
        }
      });
    }
  });

  return mappedReturns;
}
